// "How is it going, and do I need to buy anything?" — the CONVERSION half of the
// `status` answer, and the one verdict derived from it (arc 101 slice 12 §A2).
//
// THE TRAP THIS MODULE EXISTS FOR. `usage-rollup.ts` beside it made spend
// honest; spend is the input a customer is most willing to increase, and an
// advisor that reads a deep queue and recommends more boxes is right only when
// the boxes it already has are converting work into merges. Prod, 2026-08-31,
// `manual`-origin tasks all-time:
//
//   status       tasks   failure_kind IS NULL   attempt = 0
//   failed         776                    171           289
//   completed       50                     45            45
//   merged          11                      9             9
//
// 776 dead against 61 landed — 93% — and the failure mix is not a capacity
// shortage: platform-wide, `runner_error` is 1,474 of the failures and a further
// 971 carry NO `failure_kind` at all, every one of them at `attempt = 0`, which
// means no box ever claimed them. Twenty more boxes multiply the 776 and leave
// the 61 where it is, while billing the customer for the multiplication. So the
// answer leads with conversion and only then reaches capacity, and it says
// "more hardware will not help you right now" in as many words when that is
// true. An honest "don't buy" is what makes the "buy" worth believing.
//
// WHY THE BAR IS A COMPARISON AND NOT A THRESHOLD. {@link adviseCapacity} trips
// on `failed > merged + delivered` — more work died than landed. That is a
// sentence a reader can check against the numbers printed beside it, it needs no
// sample floor (with nothing settled it is `0 > 0` and simply does not fire),
// and it cannot drift the way a tuned percentage does. Prod's `manual` lane
// trips it 776 > 61; the `developerz-ai` account as a whole does NOT (2,370
// failed against 948 merged + 2,959 delivered), which is the correct answer for
// an account whose work is mostly landing.
//
// `failure_kind` IS READ ONLY ON `failed` ROWS, and that is not a detail. The
// column SURVIVES RECOVERY by design (`entities/task.ts`: "a `GROUP BY
// failure_kind` alone over-counts every terminal bucket by the tasks that
// recovered"), so the tally this folds is grouped by `(status, failure_kind)`
// and every non-`failed` group's kind is discarded here rather than trusted.

import {
  TASK_STALL_REMEDY,
  type TaskFailureKind,
  TaskStallKind,
  TaskStatus,
} from '../entities/task';

/**
 * How far back the conversion read looks, in days.
 *
 * A ROLLING window, NOT the billing month the usage block meters on, and the
 * difference is the whole reason this constant exists: a calendar window resets
 * to zero settled tasks at 00:00 on the 1st, and with nothing settled the
 * conversion gate cannot fire. An account whose last month was 93% dead would be
 * told to buy hardware for a day and a half every month — the exact
 * recommendation this module exists to refuse, delivered on a schedule. Spend is
 * a billing question and belongs on the billing boundary; conversion is a health
 * question and must not have one.
 *
 * 30 days because it is the shortest window that spans a full release cadence
 * here, and prod's tasks settle in minutes to hours, so the tail lost at the
 * boundary (a task minted before the window and settled inside it) is small.
 */
export const CONVERSION_WINDOW_DAYS = 30;

/**
 * One `(status, failure_kind)` group as the database returns it — the input
 * {@link rollUpConversion} folds. STATUS IS ALWAYS PRESENT: it is the grouping
 * key, so a consumer cannot read `failureKind` without knowing whether the row
 * is dead or merely stamped.
 */
export type ConversionTally = {
  status: TaskStatus;
  /**
   * `tasks.failure_kind` for this group. Meaningful ONLY where `status` is
   * `failed`; on any other status it is the last failed attempt's stamp on a row
   * that went on to succeed, and the fold discards it.
   */
  failureKind: TaskFailureKind | null;
  tasks: number;
  /** Of `tasks`, how many never held a box (`attempt = 0`). */
  unattempted: number;
};

/**
 * One row of the failure mix. `kind: null` is a real and large population — a
 * task that reached `failed` with nothing stamped — not an absence of data.
 */
export type ConversionFailure = {
  kind: TaskFailureKind | null;
  tasks: number;
  /** Of `tasks`, how many never held a box. A failure hardware cannot have caused. */
  unattempted: number;
};

/** Conversion: what the account's work turned into, and what killed the rest. */
export type ConversionRollup = {
  /**
   * `merged + delivered + failed` — the work whose story ended. `cancelled` is
   * deliberately NOT in here: it is a human or a policy withdrawing the work,
   * not an attempt that was lost, and folding it in would let an operator's
   * clean-up read as a failure or as a win depending on which side it landed.
   */
  settled: number;
  /** The PR merged. The only unambiguous win. */
  merged: number;
  /**
   * `completed`: a PR opened and this row never recorded a merge. Counted on the
   * landed side — the coding lane's product is a pull request — but published
   * separately from {@link merged}, because the gap between them is real and
   * large (prod: 2,959 delivered against 948 merged on one account) and a reader
   * who cannot see it cannot ask why.
   */
  delivered: number;
  failed: number;
  /** Withdrawn by an operator or a policy. Outside {@link settled} on purpose. */
  cancelled: number;
  /** Still moving: pending, claimed, running, or under the babysit watch. */
  inFlight: number;
  /**
   * Of {@link failed}, how many never held a box. A capacity purchase cannot
   * have prevented any of these — prod carries 971 of them platform-wide — so
   * this is the number that turns "more hardware will not help" from a judgement
   * into arithmetic.
   */
  failedUnattempted: number;
  /**
   * The failure mix, largest first, `null` kind included. UNCAPPED and therefore
   * addable: `sum(failures.tasks) === failed`, always. The array is bounded by
   * the closed {@link TaskFailureKind} vocabulary plus one, so it needs no cap —
   * and a truncated mix would break the one property that makes it checkable.
   */
  failures: ConversionFailure[];
};

/**
 * The three things a customer can actually buy. Everything else the platform can
 * be stuck on is a fix, not a purchase.
 */
export const PurchasableInput = {
  /** Not a money problem: answer the blocker, enable the repo, grant the thing. */
  Access: 'access',
  /** Top up a metered balance, or add a subscription — a plan is a lane. */
  Tokens: 'tokens',
  /** Enrol another box. */
  Vms: 'vms',
} as const;
export type PurchasableInput = (typeof PurchasableInput)[keyof typeof PurchasableInput];

/**
 * The answer to "do I need to buy anything?" — the three inputs, plus the two
 * answers that are not a purchase.
 *
 * The three purchasable members ARE {@link PurchasableInput}'s, by reference
 * rather than by retyping: the verdict is that vocabulary widened by two, and a
 * second spelling of `'tokens'` is how a recommendation and the thing it
 * recommends come to disagree.
 */
export const CapacityVerdict = {
  /**
   * More work is dying than landing. Capacity multiplies attempts, so the answer
   * is to fix the conversion rate first — and this verdict says so.
   */
  FixConversion: 'fix_conversion',
  Access: PurchasableInput.Access,
  Tokens: PurchasableInput.Tokens,
  Vms: PurchasableInput.Vms,
  /**
   * Work is flowing and nothing a purchase could clear is held. Stalls no money
   * moves (`STALL_BUYS[kind] === null`) land here too, and `because` names them
   * rather than reporting an empty board.
   */
  Nothing: 'nothing',
} as const;
export type CapacityVerdict = (typeof CapacityVerdict)[keyof typeof CapacityVerdict];

/**
 * WHICH INPUT, IF ANY, CLEARS EACH STALL REASON.
 *
 * `Record` over the closed enum, so a {@link TaskStallKind} added without
 * deciding whether money can clear it fails typecheck. `null` is the common and
 * important answer: most kinds are cleared by an action nobody can
 * buy, and grading them as a purchase is precisely how an advisor sells hardware
 * against a dependency graph.
 *
 * It grades the PURCHASE, never the remedy — {@link TASK_STALL_REMEDY} already
 * says what to do, is carried on every reason the stall block publishes, and
 * stays the one place that sentence is written.
 */
export const STALL_BUYS: Record<TaskStallKind, PurchasableInput | null> = {
  // The DAG's own ancestor died. Re-author the plan; a retry keeps the dead ref.
  [TaskStallKind.DependencyFailed]: null,
  [TaskStallKind.BlockedOnAnswer]: PurchasableInput.Access,
  // NOT a purchase. More boxes do not help: the gap is a SERVICE the repo's own gate
  // needs, and every box in the fleet is equally without it until one runs a Redis.
  // Grading it `vms` would sell hardware against a missing daemon.
  [TaskStallKind.NoServiceCapacity]: null,
  // An upstream PR has to merge. No amount of anything makes that happen.
  [TaskStallKind.UpstreamGroupUnmerged]: null,
  // An upstream merge has to go live. A deploy is the repo's own pipeline, not capacity.
  [TaskStallKind.UpstreamLegUndeployed]: null,
  // A person is pushing to the PR. Nothing bought makes them stop sooner.
  [TaskStallKind.PrQuietWindow]: null,
  // Waiting on work that is still running — legitimate and bounded.
  [TaskStallKind.DependencyPending]: null,
  // An upstream that finished without the merge the edge waits for: a sweep heal or an
  // operator's redispatch/cancel clears it, never hardware.
  [TaskStallKind.DependencyUnreleasable]: null,
  // An operator switched the task's agent mode off. Re-enabling it is free.
  [TaskStallKind.ModeDisabled]: null,
  // Every online machine has the task's role switched off (#7008). No purchase turns an
  // operator's switch back on.
  [TaskStallKind.NoRoleMachine]: null,
  // A person paused the epic (#6748). Resuming it is free; nothing bought releases it.
  [TaskStallKind.PlanPaused]: null,
  // Pinned to the machine that saved its paused work (#6748). Another machine cannot take
  // it; its own takes it on the next pass.
  [TaskStallKind.MachinePinned]: null,
  [TaskStallKind.NoBoxOnAccount]: PurchasableInput.Vms,
  [TaskStallKind.NoUsableKeys]: PurchasableInput.Tokens,
  // #3630: every AI-dev seat is running work. NOT `vms` — the boxes are there and
  // idle ones stay idle at the cap; a seat is a subscription line ("a plan is a
  // lane"), which is what this bucket names. An operator `ai_devs` grant clears
  // it too, but the grader grades the PURCHASE.
  [TaskStallKind.SeatCapped]: PurchasableInput.Tokens,
  // NOT `tokens`, and this is the entry that has to be read beside `NoUsableKeys`
  // rather than next to it: both are the BYOK pool refusing, and only one is a
  // balance. That one is out of CREDIT, which money fixes. This one is over its
  // upstream RATE with a dated reset — the same pool, signing fine, just not this
  // minute — and a top-up buys no throughput at all. Grading it `tokens` would sell
  // credit against a clock, which is the sharpest way an advisor loses an owner's
  // trust: they pay, nothing moves, and the board still says the same thing.
  [TaskStallKind.PoolCooling]: null,
  // A key rate-limited or out of quota until a stated reset: a clock, not a balance.
  [TaskStallKind.CredentialCooling]: null,
  // The lane's SELECTED key cannot run now. Nothing to buy: the remedy is the selection.
  [TaskStallKind.SelectionWaiting]: null,
  // A PLAN LINE, unlike the cooling arm above it: every key is healthy and signing, and
  // the account is running as many streams as the plans it bought allow. Another key —
  // a second subscription — is exactly one more lane, which is what `tokens` names
  // ("a plan is a lane"), the `SeatCapped` reading applied to the key. Declaring a
  // higher capacity clears it for free when the plan already allows more, but this
  // grades the PURCHASE, and the remedy sentence says the free lever first.
  [TaskStallKind.KeySaturated]: PurchasableInput.Tokens,
  // The same-area gate serialising two tasks on one repo, by design.
  [TaskStallKind.AreaBusy]: null,
  [TaskStallKind.RepoNotOnboarded]: PurchasableInput.Access,
  // The repo's own `bin/setup` is down on every box. A new box fails it the same way;
  // the fix is in the repo (or the registry every box pulls from) — nothing to buy.
  [TaskStallKind.RepoEnvFault]: null,
  // Parked on a clock after spending its attempts with work saved; nothing to buy.
  [TaskStallKind.ResumeBackoff]: null,
  // Held after every attempt met an unprovisioned box. Another box built the same way
  // refuses the same way; the fix is provisioning, not a purchase.
  [TaskStallKind.EnvFaultExhausted]: null,
  // NOT `vms`, and this is the map's sharpest entry: the account HAS boxes and
  // every one of them is pinned `worker`. A second `worker` box changes nothing;
  // the fix is `DZ_RUNNER_MODE=universal` on a box already paid for. Grading this
  // as hardware would sell a machine that cannot take the work either.
  [TaskStallKind.NoConverseCapacity]: null,
  // #3094: one box sat the task out after refusing it on a red base; another box may
  // claim it now and the refusing one after the window. A new box would be as red on
  // a red default branch — nothing to buy.
  [TaskStallKind.BoxFaultExcluded]: null,
};

/** The capacity inputs the verdict reads, each carried from a block already computed. */
export type CapacityInputs = {
  conversion: ConversionRollup;
  /** Stall reasons as the report ranked them — kind and how much work each holds. */
  stalls: ReadonlyArray<{ kind: TaskStallKind; tasks: number }>;
  /** Pending work NO reason holds. The queue being busy rather than stuck. */
  queued: number;
  /** Boxes with a live beat, and how many of those are running something. */
  boxes: { online: number; working: number };
  /**
   * `true` when usable keys exist and every one of them is cooling — a pool that
   * cannot sign right now without being empty, which leaves work pending while
   * the pool still counts as non-zero.
   */
  keysExhausted: boolean;
};

/** The verdict, and the numbers it was reached on. */
export type CapacityAdvice = {
  verdict: CapacityVerdict;
  /**
   * One sentence naming the figures behind {@link verdict}, addressed to whoever
   * is deciding whether to spend. Prose, because the audience is a human or the
   * agent talking to one; the branchable half is `verdict` beside it.
   */
  because: string;
};

/** Sum the work held on reasons that one purchasable input would clear. */
function heldOn(
  stalls: CapacityInputs['stalls'],
  input: PurchasableInput,
): { tasks: number; kinds: TaskStallKind[] } {
  const kinds = stalls.filter((s) => STALL_BUYS[s.kind] === input);
  return { tasks: kinds.reduce((n, s) => n + s.tasks, 0), kinds: kinds.map((s) => s.kind) };
}

/** `a, b and c` — the reasons named in the sentence, never a bare count. */
function listKinds(kinds: readonly TaskStallKind[]): string {
  return kinds.length <= 1
    ? (kinds[0] ?? '')
    : `${kinds.slice(0, -1).join(', ')} and ${kinds[kinds.length - 1]}`;
}

/**
 * Fold `(status, failure_kind)` groups into the conversion answer.
 *
 * PURE and total: an empty input is a real state (an account that has run
 * nothing this window) and answers zeros — which {@link adviseCapacity} reads as
 * "nothing settled", not as "everything failed".
 *
 * Statuses are folded through {@link TaskStatus}'s own members rather than a
 * local list of strings, so a status added to the entity lands in `inFlight` —
 * the safe side — instead of vanishing from every total here.
 */
export function rollUpConversion(tallies: readonly ConversionTally[]): ConversionRollup {
  let merged = 0;
  let delivered = 0;
  let failed = 0;
  let cancelled = 0;
  let inFlight = 0;
  let failedUnattempted = 0;
  const failures = new Map<TaskFailureKind | null, ConversionFailure>();

  for (const t of tallies) {
    switch (t.status) {
      case TaskStatus.Merged:
        merged += t.tasks;
        break;
      case TaskStatus.Completed:
        delivered += t.tasks;
        break;
      case TaskStatus.Cancelled:
        cancelled += t.tasks;
        break;
      case TaskStatus.Failed: {
        failed += t.tasks;
        failedUnattempted += t.unattempted;
        const prior = failures.get(t.failureKind);
        // Two groups can share a kind only if the reader grouped on more than
        // (status, kind); merging rather than overwriting keeps the fold correct
        // either way, and keeps `sum(failures.tasks) === failed` true by
        // construction rather than by the caller's grouping being exactly right.
        failures.set(t.failureKind, {
          kind: t.failureKind,
          tasks: (prior?.tasks ?? 0) + t.tasks,
          unattempted: (prior?.unattempted ?? 0) + t.unattempted,
        });
        break;
      }
      default:
        inFlight += t.tasks;
        break;
    }
  }

  return {
    settled: merged + delivered + failed,
    merged,
    delivered,
    failed,
    cancelled,
    inFlight,
    failedUnattempted,
    failures: [...failures.values()].sort(
      // Largest first, then by kind — a stable order, so two calls over an
      // unchanged window cannot disagree about which failure leads the mix.
      // `null` sorts last within a tie, being the one kind with no name.
      (a, b) => b.tasks - a.tasks || (a.kind ?? '￿').localeCompare(b.kind ?? '￿'),
    ),
  };
}

/**
 * CONVERSION FIRST, THEN UTILIZATION — one verdict for "do I need to buy
 * anything?", in the order the answer has to be reached.
 *
 * The ladder, and every rung is a comparison a reader can re-run against the
 * numbers published beside it:
 *
 *  1. **`fix_conversion`** — `failed > merged + delivered`. UNCONDITIONALLY
 *     FIRST. This is the whole point of the module: capacity multiplies
 *     attempts, so while more work dies than lands there is no capacity purchase
 *     worth making, and the sentence says so.
 *  2. **`access`** — work is held on something no money buys. Cheapest fix,
 *     highest certainty, so it outranks both purchases.
 *  3. **`tokens`** — work is held for want of a usable key, or the pool is whole
 *     but every key in it is cooling.
 *  4. **`vms`** — work is held for want of a box, or the queue has depth and
 *     every online box is already working. That second arm is the plan's "boxes
 *     all busy, queue depth > 0" row, and it needs `online > 0`: with no box
 *     online "every online box is working" is vacuously true, and rung 2 or the
 *     `no_box_on_account` reason is the answer there.
 *  5. **`nothing`** — work is flowing and capacity is spare.
 *
 * WHY THE ORDER IS NOT "MOST HELD WORK WINS". Ranking by volume reads as
 * data-driven and is not: it would recommend a purchase over a free fix whenever
 * the purchase happened to hold one more task, and it would let two tasks held
 * on keys outrank three hundred runs dying on the box. The ladder ranks by
 * WHETHER THE MONEY WOULD WORK, which is the question being asked.
 *
 * PURE: every input is a number some other read already produced, and this
 * function asks nothing of its own.
 */
export function adviseCapacity(input: CapacityInputs): CapacityAdvice {
  const c = input.conversion;
  const landed = c.merged + c.delivered;
  if (c.failed > landed) {
    const top = c.failures[0];
    // The dominant kind rides the sentence because it is the lead the reader
    // actually needs — 1,474 `runner_error` and 971 unstamped rows are two
    // different investigations, and neither is a purchase order.
    const lead =
      top === undefined
        ? ''
        : ` Biggest killer: ${top.kind ?? 'no failure_kind recorded'} (${top.tasks}).`;
    return {
      verdict: CapacityVerdict.FixConversion,
      because:
        `${c.failed} of ${c.settled} settled tasks failed against ${landed} that landed` +
        `${c.failedUnattempted > 0 ? `, ${c.failedUnattempted} of them without ever holding a box` : ''}.` +
        `${lead} More hardware will not help you right now — it multiplies attempts, not merges. ` +
        'Fix the failure mix first, then come back to capacity.',
    };
  }

  const access = heldOn(input.stalls, PurchasableInput.Access);
  if (access.tasks > 0) {
    return {
      verdict: CapacityVerdict.Access,
      because:
        `${access.tasks} piece(s) of work are held on ${listKinds(access.kinds)}, which no purchase clears. ` +
        'Read `stalls.reasons` for what each one needs.',
    };
  }

  const tokens = heldOn(input.stalls, PurchasableInput.Tokens);
  if (tokens.tasks > 0 || input.keysExhausted) {
    return {
      verdict: CapacityVerdict.Tokens,
      because:
        tokens.tasks > 0
          ? `${tokens.tasks} piece(s) of work are held on ${listKinds(tokens.kinds)}. ` +
            // The remedy is CITED per kind, from the one map that writes it, and
            // not restated as a sentence for the whole bucket. This bucket holds
            // two members bought differently — a key is money against a metered
            // balance, a seat is money against a plan — and the one sentence that
            // served both told a `seat_capped` account, every seat busy and every
            // key healthy, to "repair the key", which clears nothing it is
            // waiting on. Citing `TASK_STALL_REMEDY` also means a kind newly
            // graded `tokens` carries its own remedy instead of inheriting one
            // nobody chose for it. This module's docblock already said the remedy
            // lives there and is written once; this is that, held to.
            `What clears each: ${tokens.kinds.map((k) => TASK_STALL_REMEDY[k]).join('; ')}.`
          : 'Every usable BYOK key is cooling, so the pool cannot sign right now. ' +
            'Top up a metered balance, or add a subscription — a plan is its own lane.',
    };
  }

  const vms = heldOn(input.stalls, PurchasableInput.Vms);
  const saturated =
    input.queued > 0 && input.boxes.online > 0 && input.boxes.working === input.boxes.online;
  if (vms.tasks > 0 || saturated) {
    return {
      verdict: CapacityVerdict.Vms,
      because:
        vms.tasks > 0
          ? `${vms.tasks} piece(s) of work are held on ${listKinds(vms.kinds)}, and work is landing. Enrol a box.`
          : `All ${input.boxes.online} online box(es) are working and ${input.queued} task(s) are queued behind them, ` +
            'while work is landing. Another box is the thing that would help.',
    };
  }

  // Everything a purchase could clear has been ruled out above, so whatever is
  // still on the stall board is held on a reason money does not move. Saying
  // "nothing is held" over it was FALSE — the verdict (buy nothing) was right
  // and the sentence sent the reader looking for work the stall board was
  // already holding. The kinds are named, from the same map that graded them.
  const unbuyable = input.stalls.filter((s) => STALL_BUYS[s.kind] === null);
  const heldTasks = unbuyable.reduce((n, s) => n + s.tasks, 0);
  return {
    verdict: CapacityVerdict.Nothing,
    because:
      c.settled === 0
        ? 'Nothing has settled in this window, so there is no conversion rate to read and nothing to buy against. ' +
          'Call `next_steps` if you are still setting up.'
        : heldTasks > 0
          ? `${landed} of ${c.settled} settled tasks landed. ${heldTasks} piece(s) of work are held on ` +
            `${listKinds(unbuyable.map((s) => s.kind))}, which no purchase clears. Buy nothing.`
          : `${landed} of ${c.settled} settled tasks landed and nothing is held. Buy nothing.`,
  };
}
